import { useMutation } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Button, Input } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { msg, t } from "@/lib/i18n";
import type { SignupOffer } from "@logiful/domain";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Auswahl fürs Angebot – Schlüssel wie in @logiful/domain (signup.ts)
const ORDERS = { lt500: msg("unter 500"), "500_2000": msg("500–2.000"), "2000_10000": msg("2.000–10.000"), gt10000: msg("über 10.000") } as const;
const SKUS = { lt50: msg("unter 50"), "50_500": msg("50–500"), "500_5000": msg("500–5.000"), gt5000: msg("über 5.000") } as const;
const CHANNELS = { shopify: "Shopify", amazon: "Amazon", ebay: "eBay", otto: "OTTO", kaufland: "Kaufland", woocommerce: "WooCommerce", shopware: "Shopware", b2b: msg("B2B/Handel"), other: msg("Sonstige") } as const;
const NEEDS = { best_before: msg("MHD/Chargen"), bulky: msg("Sperrgut"), hazmat: msg("Gefahrgut"), fba: msg("Amazon-FBA-Belieferung"), b2b: msg("B2B-Lieferungen"), returns: msg("Retourenabwicklung"), kitting: msg("Sets/Konfektionierung") } as const;
const START = { asap: msg("sofort"), "1_3m": msg("in 1–3 Monaten"), later: msg("später") } as const;

export interface SignupData {
  company: string;
  contactName: string;
  email: string;
  phone?: string;
  acceptTerms: true;
  offer?: SignupOffer;
  website?: string;
}

const errorText = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? t("Das hat nicht geklappt – bitte erneut versuchen.")) : t("Verbindung fehlgeschlagen – bitte erneut versuchen."));

/** Registrierung neuer Kunden (Website und Shopify-App): schlanke Pflichtangaben, freiwillige Angaben fürs Angebot */
export function SignupForm({ submit, submitLabel, intro }: { submit: (d: SignupData) => Promise<unknown>; submitLabel: string; intro?: string }) {
  const [f, setF] = useState({ company: "", contactName: "", email: "", phone: "", acceptTerms: false, website: "" });
  const [offer, setOffer] = useState<SignupOffer>({});
  const [showOffer, setShowOffer] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const reg = useMutation({
    mutationFn: () => {
      const o = Object.fromEntries(Object.entries(offer).filter(([, v]) => (Array.isArray(v) ? v.length : !!v))) as SignupOffer;
      return submit({
        company: f.company.trim(),
        contactName: f.contactName.trim(),
        email: f.email.trim(),
        ...(f.phone.trim() ? { phone: f.phone.trim() } : {}),
        acceptTerms: true,
        ...(Object.keys(o).length ? { offer: o } : {}),
        ...(f.website ? { website: f.website } : {}),
      });
    },
    onSuccess: () => setSentTo(f.email.trim()),
  });
  const valid = f.company.trim().length >= 2 && f.contactName.trim().length >= 2 && EMAIL.test(f.email.trim()) && f.acceptTerms;

  if (sentTo)
    return (
      <div className="mt-6">
        <div className="flex items-center gap-2 text-success font-medium"><CheckCircle2 className="size-5" /> {t("Konto angelegt")}</div>
        <p className="mt-2 text-fg-muted text-[14px]">{t("Wir haben dir eine E-Mail an {email} geschickt. Bestätige deine Adresse, um das Portal zu öffnen.", { email: sentTo })}</p>
        <p className="mt-3 text-[13px] text-fg-muted">{t("Dein Konto startet im Testbetrieb: Du kannst sofort alles einrichten – Shops verbinden, Artikel prüfen, Regeln festlegen. Ans Lager geht erst etwas, wenn Logiful nach Vertragsabschluss freischaltet.")}</p>
      </div>
    );

  const field = (k: "company" | "contactName" | "email" | "phone", label: string, type = "text", auto?: string) => (
    <label className="block">
      <span className="text-[12.5px] font-medium text-fg-muted">{label}</span>
      <Input className="mt-1" type={type} autoComplete={auto} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </label>
  );
  const select = <K extends "ordersPerMonth" | "skuCount" | "start">(k: K, label: string, opts: Record<string, string>) => (
    <label className="block">
      <span className="text-[12.5px] font-medium text-fg-muted">{label}</span>
      <select className="mt-1 w-full h-9 rounded-md border border-border bg-surface px-2 text-[14px]" value={(offer[k] as string | undefined) ?? ""} onChange={(e) => setOffer({ ...offer, [k]: e.target.value || undefined })}>
        <option value="">{t("keine Angabe")}</option>
        {Object.entries(opts).map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
      </select>
    </label>
  );
  const chips = <K extends "channels" | "needs">(k: K, label: string, opts: Record<string, string>) => {
    const cur = (offer[k] ?? []) as string[];
    return (
      <div>
        <span className="text-[12.5px] font-medium text-fg-muted">{label}</span>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {Object.entries(opts).map(([v, l]) => {
            const on = cur.includes(v);
            return (
              <button key={v} type="button" aria-pressed={on} onClick={() => setOffer({ ...offer, [k]: on ? cur.filter((x) => x !== v) : [...cur, v] })} className={`h-7 rounded-full border px-2.5 text-[12.5px] ${on ? "border-coral bg-coral/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`}>
                {t(l)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <form
      className="mt-6 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) reg.mutate();
      }}
    >
      {intro && <p className="text-[14px] text-fg-muted">{intro}</p>}
      {field("company", t("Firma"), "text", "organization")}
      {field("contactName", t("Ansprechpartner"), "text", "name")}
      {field("email", t("E-Mail"), "email", "email")}
      {field("phone", t("Telefon (optional)"), "tel", "tel")}
      {/* Lockfeld gegen Bots – für Menschen unsichtbar */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden className="hidden" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />

      <div className="rounded-lg border border-border">
        <button type="button" onClick={() => setShowOffer(!showOffer)} aria-expanded={showOffer} className="w-full flex items-center justify-between px-3 py-2.5 text-left">
          <span>
            <span className="block text-[13.5px] font-medium">{t("Angaben für euer Angebot")}</span>
            <span className="block text-[12px] text-fg-subtle">{t("Freiwillig – damit wir schneller ein passendes Angebot machen können.")}</span>
          </span>
          <ChevronDown className={`size-4 text-fg-muted transition-transform ${showOffer ? "rotate-180" : ""}`} />
        </button>
        {showOffer && (
          <div className="space-y-3 border-t border-border px-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              {select("ordersPerMonth", t("Aufträge pro Monat"), ORDERS)}
              {select("skuCount", t("Anzahl Artikel"), SKUS)}
            </div>
            {chips("channels", t("Wo verkauft ihr?"), CHANNELS)}
            {chips("needs", t("Besonderheiten"), NEEDS)}
            {select("start", t("Gewünschter Start"), START)}
            <label className="block">
              <span className="text-[12.5px] font-medium text-fg-muted">{t("Noch etwas, das wir wissen sollten? (optional)")}</span>
              <textarea className="mt-1 w-full rounded-md border border-border bg-surface px-2.5 py-2 text-[14px] min-h-[70px]" maxLength={1000} value={offer.note ?? ""} onChange={(e) => setOffer({ ...offer, note: e.target.value || undefined })} />
            </label>
          </div>
        )}
      </div>

      <label className="flex items-start gap-2 text-[13px] text-fg-muted">
        <input type="checkbox" className="mt-0.5" checked={f.acceptTerms} onChange={(e) => setF({ ...f, acceptTerms: e.target.checked })} />
        <span>
          {t("Ich akzeptiere die")} <a className="underline hover:text-fg" href="https://logiful.de/agb" target="_blank" rel="noreferrer">{t("AGB")}</a> {t("und habe die")} <a className="underline hover:text-fg" href="https://logiful.de/datenschutz" target="_blank" rel="noreferrer">{t("Datenschutzerklärung")}</a> {t("gelesen.")}
        </span>
      </label>
      {reg.isError && <p className="text-[13px] text-danger">{errorText(reg.error)}</p>}
      <Button type="submit" variant="primary" className="w-full h-10" disabled={!valid} loading={reg.isPending}>
        {submitLabel} <ArrowRight className="size-4" />
      </Button>
      <p className="text-[12px] text-fg-subtle">{t("Startet im Testbetrieb – es geht nichts ans Lager, bis Logiful freischaltet.")}</p>
    </form>
  );
}
